export type RealtimeRefresh = () => void | Promise<void>;
export type RealtimeEvent = Record<string, unknown> & { type: string };

// A socket is an invalidation hint, not a replayable message history. Reconcile
// after joining/rejoining and periodically while a ticket remains visible.
export function connectRealtimeRoom(url: string, refresh: RealtimeRefresh, pollMs = 0, onEvent?: (event: RealtimeEvent) => void) {
  let socket: WebSocket | null = null;
  let retryTimer: number | undefined;
  let connectionTimer: number | undefined;
  let pongTimer: number | undefined;
  let attempts = 0;
  let openedAt = 0;
  let stopped = false;
  let refreshing = false;
  let refreshAgain = false;
  const visible = () => !stopped && document.visibilityState !== "hidden";

  const reconcile = async () => {
    if (!visible()) return;
    if (refreshing) { refreshAgain = true; return; }
    refreshing = true;
    try { await refresh(); }
    catch { /* A transient fetch failure is retried by the next visible poll/event. */ }
    finally {
      refreshing = false;
      if (refreshAgain) { refreshAgain = false; void reconcile(); }
    }
  };
  const clearSocketTimers = () => {
    window.clearTimeout(connectionTimer);
    window.clearTimeout(pongTimer);
    connectionTimer = pongTimer = undefined;
  };
  const disconnect = () => {
    window.clearTimeout(retryTimer);
    retryTimer = undefined;
    clearSocketTimers();
    const old = socket;
    socket = null; // Ignore delayed events from sockets that no longer own this room.
    try { old?.close(1000, "Connection replaced"); } catch { /* already closed */ }
  };
  const retry = () => {
    disconnect();
    if (!visible()) return;
    const delay = Math.min(30_000, 750 * 2 ** Math.min(++attempts, 6));
    retryTimer = window.setTimeout(() => { retryTimer = undefined; connect(); }, delay);
  };
  const connect = () => {
    if (!visible() || (socket && socket.readyState <= WebSocket.OPEN)) return;
    window.clearTimeout(retryTimer);
    retryTimer = undefined;
    let current: WebSocket;
    try { current = new WebSocket(url); }
    catch { retry(); return; }
    socket = current;
    const active = () => !stopped && socket === current;
    connectionTimer = window.setTimeout(() => { if (active()) retry(); }, 15_000);
    current.addEventListener("open", () => {
      if (!active()) return;
      window.clearTimeout(connectionTimer);
      connectionTimer = undefined;
      openedAt = Date.now();
      // Do not reset backoff on a handshake that immediately closes again.
      void reconcile();
    });
    current.addEventListener("message", (event) => {
      if (!active()) return;
      if (event.data === "pong") {
        window.clearTimeout(pongTimer);
        pongTimer = undefined;
        return;
      }
      try {
        const payload = JSON.parse(String(event.data)) as { type?: unknown };
        if (typeof payload?.type === "string" && !payload.type.startsWith("presence.") && payload.type !== "realtime.connected") {
          onEvent?.(payload as RealtimeEvent);
          void reconcile();
        }
      } catch { /* Ignore malformed frames. */ }
    });
    current.addEventListener("close", () => {
      if (!active()) return;
      if (openedAt && Date.now() - openedAt >= 30_000) attempts = 0;
      openedAt = 0;
      retry();
    });
    current.addEventListener("error", () => { if (active()) retry(); });
  };
  const resume = () => {
    if (!visible()) return;
    void reconcile();
    connect();
  };
  const visibility = () => { if (visible()) resume(); else disconnect(); };
  const heartbeat = window.setInterval(() => {
    if (!visible() || !socket || socket.readyState !== WebSocket.OPEN || pongTimer !== undefined) return;
    try {
      socket.send("ping");
      pongTimer = window.setTimeout(retry, 10_000);
    } catch { retry(); }
  }, 20_000);
  const poll = pollMs ? window.setInterval(() => { void reconcile(); }, pollMs) : undefined;
  document.addEventListener("visibilitychange", visibility);
  window.addEventListener("online", resume);
  window.addEventListener("focus", resume);
  window.addEventListener("pageshow", resume);
  connect();
  return () => {
    stopped = true;
    disconnect();
    window.clearInterval(heartbeat);
    window.clearInterval(poll);
    document.removeEventListener("visibilitychange", visibility);
    window.removeEventListener("online", resume);
    window.removeEventListener("focus", resume);
    window.removeEventListener("pageshow", resume);
  };
}
